import { type BigIntLike, formatOraclePrice } from '@repo/utils'

import { UNKNOWN_SYMBOL } from './getMarketName'

interface OraclePriceAsset {
  symbol?: string | null
  decimals?: number | null
}

export interface GetOraclePriceLabelParams {
  collateralAsset?: OraclePriceAsset | null
  loanAsset?: OraclePriceAsset | null
  /** On-chain oracle price (`market.state.price`), scaled by 36 - collateralDecimals + loanDecimals. */
  price?: BigIntLike | null
}

/**
 * Builds the "1 COLLATERAL = price LOAN" label for a market's on-chain oracle price,
 * matching the Fixed Rate Markets convention (e.g. "1 cbBTC = 80,000 USDC").
 *
 * The oracle price is a raw collateral/loan ratio read from the contract; it does
 * not depend on third-party (DefiLlama) pricing, so it is shown whenever `price`
 * and both assets' decimals are available. Returns "-" when any of those is missing or the
 * price is malformed.
 *
 * Missing symbols fall back to `UNKNOWN_SYMBOL`. Ratios can span extreme magnitudes
 * (e.g. ~4e-27 for an exotic loan asset); tiny values are floored to "< 0.00000001"
 * instead of a wall of zeros, matching the rest of the app (see MigrationAmountModal).
 */
export const getOraclePriceLabel = ({
  collateralAsset,
  loanAsset,
  price
}: GetOraclePriceLabelParams): string => {
  const priceString = formatOraclePrice({
    value: price,
    collateralDecimals: collateralAsset?.decimals,
    loanDecimals: loanAsset?.decimals
  })

  if (priceString === undefined) return '-'

  const collateralSymbol = collateralAsset?.symbol || UNKNOWN_SYMBOL
  const loanSymbol = loanAsset?.symbol || UNKNOWN_SYMBOL

  return `1 ${collateralSymbol} = ${priceString} ${loanSymbol}`
}
